<template>
  <div class="rule-engine-wrapper">
    <!-- 0. Login/Guest -->
    <LoginGuest
      v-if="currentStep === StepRoute['LoginGuest']"
      :is-guest-allowed="isGuestAllowed"
      @login="handleLogin"
      @guest="handleGuestContinue"
      @support-request:cancel="emitCancelSupportWithConfig"
    />
    <!-- 1. Guest -->
    <GuestForm
      v-else-if="currentStep === StepRoute['GuestForm']"
      :guest-saved-data="savedDataToPass.guest"
      :connect-config="connectConfig"
      :is-open-request-flow="isOpenRequestFlow"
      :request-info="requestInfo"
      @submit:detail="onSubmitGuestForm"
      @support-request:cancel="emitCancelSupportWithConfig"
    />
    <!-- 2. Transactions depends on actionType -->

    <Transaction
      v-else-if="currentStep === StepRoute['Transaction'] && actionType !== 3 && !loading"
      :connect-config="connectConfig"
      :ticket-fields="ticketFields"
      :request-info="requestInfo"
      :user-selections="userSelections"
      :transaction-type="actionType"
      :transactions-saved-data="savedDataToPass.transactions"
      :external-transaction-kinds="externalTransactionKinds"
      :transaction-flow="transactionFlow"
      :on-show-request-limit-modal="onShowRequestLimitModal"
      @end-chat="endChat"
      @transaction:input="handleTransactionInput"
      @support-request:cancel="emitCancelSupportWithConfig"
      @remedy-transaction:input="handleRemedyTransactionInput"
      @direct-open-request="emitDirectOpenRequest"
      @open-active-conversation="emitOpenActiveConversation"
      @transaction:metadata-input="handleTransactionWithMetadataRuleApplied"
      @show-overlay-trigger="showOverlayTrigger"
      @transaction:ai-flow-input="handleTransactionAiFlowInput"
    />
    <!-- // TODO: [Sportybet] -->
    <BetTransaction
      v-else-if="currentStep === StepRoute['Transaction'] && actionType === 3 && !loading"
      :connect-config="connectConfig"
      :ticket-fields="ticketFields"
      :request-info="requestInfo"
      :user-selections="userSelections"
      :transaction-type="actionType"
      :transactions-saved-data="savedDataToPass.transactions"
      :external-transaction-kinds="externalTransactionKinds"
      :transaction-flow="transactionFlow"
      :on-show-request-limit-modal="onShowRequestLimitModal"
      @end-chat="endChat"
      @transaction:input="handleTransactionInput"
      @support-request:cancel="emitCancelSupportWithConfig"
      @direct-open-request="emitDirectOpenRequest"
      @open-active-conversation="emitOpenActiveConversation"
      @transaction:ai-flow-input="handleTransactionAiFlowInput"
    />
    <!-- 3. LoggedIn Form -->
    <LoggedInForm
      v-else-if="currentStep === StepRoute['LoggedInForm']"
      :issue-msg="issueMsg"
      :show-back="!!(isLogin && actionType && !appliedMetadataAutomation)"
      :ticket-subject="ticketSubject"
      :logged-in-form-saved-data="savedDataToPass.loggedInForm"
      :connect-config="connectConfig"
      @fillDetail="handleLoggedInform"
      @back="handleBackLogin"
      @support-request:cancel="emitCancelSupportWithConfig"
    />
    <!-- 4. LiveChat/ Call/ Open Ticket -->
    <ContactOptions
      v-else-if="currentStep === StepRoute['ContactOptions']"
      :show-logged-in-options="showLoggedInOptions"
      :ticket-fields="ticketFields"
      :request-info="requestInfo"
      :show-add-comment="fromActiveConversation"
      :show-back="showContactOptionsBackButton"
      :connect-config="connectConfig"
      :user-selections="userSelections"
      :show-threshold-limit-modal="showThresholdLimitModal"
      :threshold-limit-requested-channel="thresholdLimitRequestedChannel"
      :active-conversation-ticket="activeConversationTicket"
      :on-show-request-limit-modal="onShowRequestLimitModal"
      @update:request-info="updateRequestInfo"
      @connect:request="handleContactOption"
      @back="handleBackContactOptions"
      @support-request:cancel="emitCancelSupportWithConfig"
      @set-show-threshold-limit-modal="setShowThresholdLimitModal"
      @guest-open-request="handleGuestOpenRequest"
    />
    <BottomWrapper
      v-else
      id="rule-engine-bottom-wrapper"
      v-waiting.center="isLoading = { isLoading: loading }"
      :buttons="buttonMap[currentStep]"
      :cancelable-request="currentStep !== StepRoute.OpenRequestEnd"
      @select="bottomWrapperHandler"
      @support-request:cancel="emitCancelSupportWithConfig"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator';
import * as PleasedModel from '@/common/api/models.pleased';
import { TransactionType } from '@/common/config';
import Input from '@/components/Input.vue';
import { Button, BottomWrapper } from '../BottomWrapper.vue';
import LoginGuest from './LoginGuest.vue';
import GuestForm from './GuestForm/GuestForm.vue';
import Transaction from './Transaction/Transaction.vue';
import BetTransaction from './Transaction/BetTransaction.vue';
import LoggedInForm from './LoggedInForm/LoggedInForm.vue';
import ContactOptions from './ContactOptions.vue';
import { namespace } from 'vuex-class';
import { emitOpenMyRequest } from '@/common/eventBus';
import waiting from '@/components/loading/waiting';
import { Bridge } from '@/common/bridge';
import {
  FormField,
  ConnectRequestEvent,
  TransactionInputEvent,
  VisitorInfoFilled,
  DynamicFieldData,
  RemedyTransactionInputEvent,
  PleasedGuestUserInfo,
} from '@/common/types';
import {
  ApplyMetadataAutomationExternEvent,
  ConnectToAgent,
  UserInputSubmittedTransactionForAiEvent,
} from '@/common/bot/types';
import { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { switcher } from '@/common/bot/flow-frameworks/switcher';
import { Conversation } from '../active-conversations/type';
import { MatchedChatTrigger } from '@/common/bot/flow-frameworks/chat-trigger';
import { mixins } from 'vue-class-component';
import LogPageEventMixin, { PAGE_EVENTS, PAGE_TRACKING } from '@/mixins/logPageEvent';
import { analytics } from '@/common/logs';
import { BTN_EVTS } from '@/common/logs/events';
import { logger } from '@/common/api/logger';

const userModule = namespace('user');

export enum StepRoute {
  EndChat, // help_Chat-10
  LoginGuest, // help_Chat-6
  GuestForm, // help_Chat-open ticket without login
  // Login, // help_Chat-7
  Transaction, // help_Chat-8
  LoggedInForm, // help_Chat-8a
  ContactOptions, // help_Chat-9
  OpenRequestEnd,
}

export interface StepChangeEvent {
  step: StepRoute;
  previous: StepRoute;
}

interface SavedDataToPass {
  transactions: TransactionInputEvent | null;
  guest: VisitorInfoFilled | null;
  loggedInForm: VisitorInfoFilled | null;
}

enum buttonKeyMap {
  Yes = 'yes',
  No = 'no',
  End = 'end',
  // Login = 'login'
}

const buttonMaker = (name: string, id = name): Button => ({ name, id, isActive: true });

@Component({
  components: {
    BottomWrapper,
    Input,
    LoginGuest,
    GuestForm,
    Transaction,
    LoggedInForm,
    ContactOptions,
    BetTransaction,
  },
  directives: {
    waiting,
  },
})
export default class RuleENgineHelper extends mixins(LogPageEventMixin) {
  @Prop() readonly actionableItemKey!: number;
  @Prop() readonly showLoggedInOptions!: { showCall: boolean; showLiveChat: boolean; showOpenRequest: boolean };
  @Prop({ default: false }) readonly isGuestAllowed!: boolean;
  @Prop({ default: '' }) readonly issueMsg!: '';
  @Prop({ default: '' }) readonly ticketSubject!: string;
  @Prop(Boolean) readonly fromActiveConversation!: boolean;
  @Prop() readonly connectConfig!: ConnectToAgent | null;
  @Prop() readonly userSelections!: PleasedModel.UserSelection[];
  @Prop() readonly showThresholdLimitModal!: boolean;
  @Prop({ default: null }) readonly thresholdLimitRequestedChannel!: 'liveChat' | 'call' | null;
  @Prop() readonly activeConversationTicket!: Conversation;
  @Prop() readonly aiShowSelectorType!: ExternalTransactionKind[] | null;
  @Prop() readonly canConnectToAI!: boolean;
  @Prop() readonly onShowRequestLimitModal!: () => void;

  @userModule.State('isLogin') isLogin!: boolean;
  @userModule.State('guestUser') guestUser!: PleasedGuestUserInfo;
  @userModule.State('guestFormData') guestFormData!: {
    name: string;
    email: string;
    phone: string;
    description: string;
  } | null;
  @userModule.Mutation('updateLoginStatus') updateLoginStatus!: Function;
  @userModule.Mutation('setGuestFormData') setGuestFormData!: (payload: {
    name: string;
    email: string;
    phone: string;
    description: string;
  }) => void;

  StepRoute = StepRoute;

  get buttonMap(): { [index: number]: Button[] } {
    return {
      [StepRoute.EndChat]: [buttonMaker(this.$t('global.end_chat') as string)],
      // [StepRoute.LoginGuest]: [],
      [StepRoute.OpenRequestEnd]: [buttonMaker(this.$t('global.end_chat') as string, buttonKeyMap.End)],
    };
  }
  actionMap = {
    [StepRoute.EndChat]: this.endChat,
    // [StepRoute.LoginGuest]: this.handleLoginGuest,
    [StepRoute.GuestForm]: () => {},
    [StepRoute.Transaction]: () => {},
    [StepRoute.OpenRequestEnd]: this.handleLoggedInRequestEnd,
  };
  currentStep: StepRoute | null = null;
  prevStep: StepRoute | null = null;
  isOpenRequestFlow = false;

  savedDataToPass: SavedDataToPass = {
    transactions: null,
    guest: null,
    loggedInForm: null,
  };

  loading = false;
  // Transactions
  actionType: TransactionType | null = null;
  fieldData: DynamicFieldData | null = null;
  guestDescription = '';
  requestInfo: { subject: string; description: string; email: string } = { subject: '', description: '', email: '' };

  get staticFormFields(): FormField[] {
    return [
      {
        id: -10000,
        displayName: 'Description',
        placeholder: 'Describe your problem',
        name: 'description',
        required: true,
      },
      {
        id: -10001,
        displayName: 'Subject',
        placeholder: 'Subject',
        name: 'subject',
        required: false,
      },
    ];
  }

  get [PAGE_TRACKING]() {
    if (this.fromActiveConversation) {
      return null;
    }

    return {
      name: PAGE_EVENTS.CONNECT_REQUEST,
    };
  }

  apiFormFields: FormField[] = [];

  get bottomWrapperHandler() {
    const { actionMap, currentStep } = this;
    return actionMap[currentStep as keyof typeof actionMap] || (() => {});
  }

  get allFormFields(): FormField[] {
    return [...this.staticFormFields, ...this.apiFormFields];
  }

  get ticketFields(): PleasedModel.TicketField[] {
    return this.fieldData && this.fieldData.fields
      ? this.fieldData.fields.filter((f) => f.id > 0).map((f) => ({ fieldId: f.id, fieldValue: f.value }))
      : [];
  }

  // TODO: maybe add more flows here, create a map
  get transactionFlow() {
    const isAiFlow = this.aiShowSelectorType && this.aiShowSelectorType.length > 0;
    return isAiFlow ? 'ai' : 'default';
  }

  get externalTransactionKinds() {
    if (this.aiShowSelectorType) {
      return this.aiShowSelectorType;
    }

    // Handle case with outdated field
    if (!this.connectConfig?.transactionTypes?.length && this.connectConfig?.transactionType) {
      return [this.connectConfig.transactionType];
    }

    return this.connectConfig?.transactionTypes || [];
  }

  get appliedMetadataAutomation() {
    return this.connectConfig?.appliedMetadataAutomation;
  }

  get showContactOptionsBackButton() {
    return !this.fromActiveConversation && this.connectConfig?.showDescriptionBox;
  }

  created() {
    const { externalTransactionKinds } = this;

    const externalTransactionKind = this.externalTransactionKinds.length > 0 ? externalTransactionKinds[0] : null;

    this.actionType = externalTransactionKind
      ? switcher<ExternalTransactionKind, TransactionType | null>()
          .when(
            (type) => type.startsWith('DEPOSIT'),
            () => TransactionType.Deposit
          )
          .when(
            (type) => type.startsWith('WITHDRAW'),
            () => TransactionType.Withdrawal
          )
          .when(
            (type) => type.startsWith('BET'),
            () => TransactionType.Bet
          )
          .none((type) => {
            logger.error('[TRANSACTION] Transaction type not identified', { type });
            return null;
          })
          .match(externalTransactionKind as ExternalTransactionKind)
      : null;

    if (this.fromActiveConversation) {
      this.currentStep = StepRoute['ContactOptions'];
      return;
    }

    // means we returned to this page from overloaded error
    if (this.showThresholdLimitModal) {
      this.currentStep = StepRoute['ContactOptions'];
      return;
    }

    // Only NOT resolved will go to RuleEnginHelper
    const isUserIdentified = this.isLogin || (!this.connectConfig?.loginRequired && this.guestUser.filledDetails);
    if (isUserIdentified) {
      this.resolveTransactionStep();
      return;
    }

    Bridge.listenFromParent('LOGIN_POP', (data: { isLogin: boolean; token: string }) => {
      if (this.currentStep === StepRoute['LoginGuest']) {
        this.updateLoginStatus(data);
        this.handleLogin();
      }
    });

    this.currentStep = StepRoute['LoginGuest'];
  }

  resolveTransactionStep() {
    if (this.fromActiveConversation) {
      this.currentStep = StepRoute.ContactOptions;
      return;
    }

    const isSelectorEnabled =
      this.aiShowSelectorType?.length || (!this.connectConfig?.appliedMetadataAutomation && this.actionType);

    const showTransactions = this.isLogin && isSelectorEnabled;

    if (showTransactions) {
      this.currentStep = StepRoute['Transaction'];
    } else {
      // AI TODO: maybe remove this condition and set subject if it's empty
      if (this.connectConfig?.appliedMetadataAutomation || this.connectConfig?.aiConversation) {
        // with applied metadata automation we should have this data
        this.requestInfo.subject = this.ticketSubject;
      }

      if (this.connectConfig?.showDescriptionBox) {
        this.currentStep = StepRoute['LoggedInForm'];
      } else {
        this.currentStep = StepRoute['ContactOptions'];
      }
    }
  }

  @Watch('currentStep')
  onStepChange(step: StepRoute, previous: StepRoute) {
    this.prevStep = previous;
    this.$emit('step:changed', { step, previous } as StepChangeEvent);
  }

  updated() {
    this.$emit('updated');
  }

  /**
   * @event GuestForm#submit:detail
   */
  onSubmitGuestForm($event: VisitorInfoFilled) {
    const { visitorInfo, description } = $event;
    this.guestDescription = description;

    this.requestInfo.subject = this.ticketSubject;
    this.requestInfo.description = description;
    this.requestInfo.email = visitorInfo.email;

    this.savedDataToPass.guest = $event;

    // Save to Vuex store
    this.setGuestFormData({
      name: $event.visitorInfo.name || '',
      email: $event.visitorInfo.email,
      phone: $event.visitorInfo.phone || '',
      description: $event.description,
    });

    this.$emit('guest:login-successful', $event);

    // Do no change route as AI will handle
    if (this.canConnectToAI) return;

    this.currentStep = StepRoute['ContactOptions'];
  }

  /**
   * @event ContactOption#connect:request
   */
  handleContactOption($event: ConnectRequestEvent) {
    this.$emit('connect:request', $event);
    if ($event.type === 'openRequest') {
      this.currentStep = StepRoute['OpenRequestEnd'];
    }
  }

  /**
   * Handle guest user clicking continue as guest - go to GuestForm (first time)
   */
  handleGuestContinue() {
    this.isOpenRequestFlow = false;

    // Track continue as guest click
    analytics.trackClick({
      name: BTN_EVTS.GUEST_CONTINUE_AS_GUEST_CLICKED,
      metadata: {
        flow: 'continue_as_guest',
      },
    });

    this.currentStep = StepRoute['GuestForm'];
  }

  /**
   * Handle guest user clicking open request - go to GuestForm (second time)
   */
  handleGuestOpenRequest(data: { description: string }) {
    this.isOpenRequestFlow = true;

    // Track open request flow start
    analytics.trackClick({
      name: BTN_EVTS.GUEST_OPEN_REQUEST_FLOW_STARTED,
      metadata: {
        flow: 'open_request',
      },
    });

    // Try to restore guest data from Vuex store if component was remounted
    if (!this.savedDataToPass.guest && this.guestFormData) {
      this.savedDataToPass.guest = {
        visitorInfo: {
          name: this.guestFormData.name,
          email: this.guestFormData.email,
          phone: this.guestFormData.phone,
          displayName: this.guestFormData.name,
        },
        description: data.description,
      };
    }

    this.currentStep = StepRoute['GuestForm'];
  }

  handleBackLogin() {
    this.currentStep = StepRoute['Transaction'];
  }

  handleBackContactOptions() {
    this.currentStep = this.prevStep;
  }

  endChat() {
    this.$emit('endChat');
  }

  // Login
  handleLogin() {
    this.$emit('login:successful');
    // Do no change route as AI will handle
    if (this.canConnectToAI) return;

    this.resolveTransactionStep();
  }

  handleTransactionInput($event: TransactionInputEvent) {
    this.savedDataToPass.transactions = $event;
    this.requestInfo.subject = this.ticketSubject;
    this.$emit('transaction:input', $event);

    const shouldUserEnterDetails = this.connectConfig?.showDescriptionBox || $event.selectedTransIssue.length === 0;
    this.currentStep = shouldUserEnterDetails ? StepRoute['LoggedInForm'] : StepRoute['ContactOptions'];
  }

  handleRemedyTransactionInput($event: RemedyTransactionInputEvent) {
    this.$emit('remedy-transaction:input', $event);
  }

  emitCancelSupportWithConfig() {
    this.$emit('support-request:cancel', this.connectConfig);
  }

  emitDirectOpenRequest(ticket: PleasedModel.TicketDetail) {
    this.$emit('direct-open-request', ticket);
  }

  emitOpenActiveConversation(conversation: Conversation) {
    this.currentStep = StepRoute['ContactOptions'];
    this.$emit('open-active-conversation', conversation);
  }

  // LoggedInForm
  handleLoggedInform(data: VisitorInfoFilled & DynamicFieldData) {
    const { visitorInfo, description = '', fields } = data;

    this.fieldData = { fields };

    this.requestInfo.subject = this.ticketSubject;
    this.requestInfo.description = description;
    this.requestInfo.email = visitorInfo.email;

    this.savedDataToPass.loggedInForm = { visitorInfo, description };

    this.$emit('logged-in-form-filled', { visitorInfo, description });
    this.currentStep = StepRoute['ContactOptions'];
  }

  updateRequestInfo(requestInfo: { subject: string; description: string; email: string }) {
    this.requestInfo = { ...requestInfo };
  }

  handleLoggedInRequestEnd(button: Button) {
    if (button.id === buttonKeyMap.End) {
      this.endChat();
    } else {
      emitOpenMyRequest(true);
    }
  }

  setShowThresholdLimitModal(show: boolean) {
    this.$emit('set-show-threshold-limit-modal', show);
  }

  handleTransactionAiFlowInput(event: UserInputSubmittedTransactionForAiEvent) {
    this.$emit('transaction:ai-flow-input', event);
  }

  handleTransactionWithMetadataRuleApplied(data: {
    transactionEvent: TransactionInputEvent;
    metadataEvent: ApplyMetadataAutomationExternEvent;
  }) {
    const { transactionEvent, metadataEvent } = data;

    this.handleTransactionInput(transactionEvent);
    this.$emit('metadata-rule-applied', metadataEvent);
  }

  showOverlayTrigger(match: MatchedChatTrigger) {
    this.$emit('show-overlay-trigger', match);
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';
.rule-engine-wrapper {
  z-index: 2;
  position: relative;
}
</style>
